import ru from './ru.json'

/**
 * The interface language is Russian and every string lives in `ru.json`, so that
 * adding English and Italian later is a matter of dropping in two more files
 * rather than grepping through components for Cyrillic.
 */
const catalogue = ru

type Catalogue = typeof catalogue

/** Every dot-path that ends at a string, e.g. `"signIn.title"`. */
type LeafPaths<T> = T extends string
  ? never
  : {
      [K in keyof T & string]: T[K] extends string ? K : `${K}.${LeafPaths<T[K]>}`
    }[keyof T & string]

export type MessageKey = LeafPaths<Catalogue>

/** Values substituted into `{placeholder}` slots. */
export type MessageParams = Readonly<Record<string, string | number>>

function lookup(key: string): string | undefined {
  let node: unknown = catalogue

  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined
    node = (node as Record<string, unknown>)[segment]
  }

  return typeof node === 'string' ? node : undefined
}

function interpolate(template: string, params: MessageParams | undefined): string {
  if (params === undefined) return template

  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name]
    return value === undefined ? whole : String(value)
  })
}

/**
 * Translate a key. A missing key returns the key itself rather than an empty
 * string: a screen reading `signIn.title` is an obvious bug, a blank one is a
 * mystery.
 */
export function t(key: MessageKey, params?: MessageParams): string {
  const template = lookup(key)
  if (template === undefined) {
    console.warn(`i18n: missing key "${key}"`)
    return key
  }
  return interpolate(template, params)
}

/**
 * Render an error message that the API produced.
 *
 * The server answers in English prose ("the email or password is wrong"). We
 * show the Russian rendering of *that exact message* and fall back to the raw
 * text when we have not seen it before — so a new server error still reaches the
 * person instead of being flattened into "что-то пошло не так".
 *
 * This is a translation, never a reinterpretation. In particular the sign-in
 * failure stays deliberately undifferentiated: the server refuses to say whether
 * the account exists, and neither do we.
 */
export function translateApiMessage(message: string): string {
  const known: Record<string, string> = catalogue.api
  return known[message] ?? message
}
